import { useQuery } from '@tanstack/react-query';

import { fetchStudioDefaultPaymentProviderAPI } from '~entities/studios/api/get-studio-default-payment-provider';
import { PaymentMethod } from '~entities/wallet/model/types';

import { baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';
import { isSquarePaymentProvider } from '~shared/lib/square-web-payments';

export type OnlinePaymentMethod = 'card' | 'link' | 'us_bank_account' | 'au_becs_debit';

const ONLINE_PAYMENT_METHODS = new Set<string>([
  PaymentMethod.CARD,
  PaymentMethod.LINK,
  PaymentMethod.US_BANK_ACCOUNT,
  PaymentMethod.AU_BECS_DEBIT,
]);

/** True when `type` is a selectable online payment method (card / Link / ACH / BECS). */
export const isOnlinePaymentMethod = (
  type: string | undefined,
): type is OnlinePaymentMethod =>
  !!type && ONLINE_PAYMENT_METHODS.has(type);

export interface AllowedPaymentMethodsResponse {
  paymentMethodTypes: string[];
  paymentMethodConfigId?: string;
  paymentMethodConfigName?: string;
  source?: 'stripe' | 'internal';
  bypassStripePaymentMethodConfig?: boolean;
}

export const getAllowedPaymentMethods = async (
  locationId: string,
): Promise<string[]> => {
  const { paymentProvider } = await fetchStudioDefaultPaymentProviderAPI();
  if (isSquarePaymentProvider(paymentProvider)) {
    return [PaymentMethod.CARD];
  }

  const response = await baseAPI.get<AllowedPaymentMethodsResponse>(
    '/payment-processor/allowed-payment-methods',
    {
      params: { locationId },
    },
  );
  return response.data?.paymentMethodTypes ?? [];
};

export const getAllowedPaymentMethodsConfig = async (
  locationId: string,
): Promise<AllowedPaymentMethodsResponse> => {
  const response = await baseAPI.get<AllowedPaymentMethodsResponse>(
    '/payment-processor/allowed-payment-methods',
    {
      params: { locationId },
    },
  );
  return response.data ?? { paymentMethodTypes: [] };
};

export const useGetAllowedPaymentMethods = (locationId: string) => {
  return useQuery({
    queryKey: [KEYS.ALLOWED_PAYMENT_METHODS, locationId],
    queryFn: () => getAllowedPaymentMethods(locationId),
    enabled: !!locationId,
  });
};

export const useGetAllowedPaymentMethodsConfig = (locationId: string) => {
  return useQuery({
    queryKey: [KEYS.ALLOWED_PAYMENT_METHODS, locationId, 'config'],
    queryFn: () => getAllowedPaymentMethodsConfig(locationId),
    enabled: !!locationId,
  });
};

export const isPaymentMethodAllowed = (
  allowedMethods: string[] | undefined,
  method: OnlinePaymentMethod,
): boolean => {
  if (!Array.isArray(allowedMethods) || allowedMethods.length === 0) {
    return false;
  }
  return allowedMethods.includes(method);
};

export const PAYMENT_METHOD_LABELS: Record<
  OnlinePaymentMethod,
  { short: string; long: string }
> = {
  card: { short: 'Credit Card', long: '💳 Credit Card' },
  link: { short: 'Stripe Link', long: '🔗 Stripe Link' },
  us_bank_account: { short: 'Bank Transfer (ACH)', long: '🏦 ACH Transfer' },
  au_becs_debit: { short: 'Direct Debit (BECS)', long: '🏦 Direct Debit (BECS)' },
};

export interface PaymentMethodOption {
  method: OnlinePaymentMethod;
  label: string;
  show: boolean;
}

export const usePaymentMethodOptions = (locationId: string) => {
  const { data: allowedMethods, isLoading } =
    useGetAllowedPaymentMethods(locationId);

  const paymentOptions: PaymentMethodOption[] = [
    {
      method: 'card',
      label: PAYMENT_METHOD_LABELS.card.long,
      show: isPaymentMethodAllowed(allowedMethods, 'card'),
    },
    {
      method: 'us_bank_account',
      label: PAYMENT_METHOD_LABELS.us_bank_account.long,
      show: isPaymentMethodAllowed(allowedMethods, 'us_bank_account'),
    },
    {
      method: 'au_becs_debit',
      label: PAYMENT_METHOD_LABELS.au_becs_debit.long,
      show: isPaymentMethodAllowed(allowedMethods, 'au_becs_debit'),
    },
  ];

  const isCardAllowed = isPaymentMethodAllowed(allowedMethods, 'card');
  const isAchAllowed = isPaymentMethodAllowed(allowedMethods, 'us_bank_account');
  const isBecsAllowed = isPaymentMethodAllowed(
    allowedMethods,
    'au_becs_debit',
  );

  const firstAllowedMethod: OnlinePaymentMethod | undefined = isCardAllowed
    ? 'card'
    : isAchAllowed
      ? 'us_bank_account'
      : isBecsAllowed
        ? 'au_becs_debit'
        : undefined;

  return {
    paymentOptions,
    isCardAllowed,
    isAchAllowed,
    isBecsAllowed,
    firstAllowedMethod,
    isLoading,
    allowedMethods,
  };
};
